// ============================================================================
// BOQ — clauses: reusable library + per-project inclusions / exclusions
// ============================================================================
// boq.clause_library is the shared template set; boq.project_clauses are the
// clauses chosen/edited for one project's bill cover. Adding from the library
// copies the text so editing a template later doesn't change issued bills.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { formatSupabaseError } from '../../hooks/useSupabase';

export const CLAUSE_KINDS = ['inclusion', 'exclusion', 'assumption', 'qualification'];
export const CLAUSE_LABEL = {
  inclusion: 'Inclusions',
  exclusion: 'Exclusions',
  assumption: 'Assumptions',
  qualification: 'Qualifications',
};

const mapLibrary = (c) => ({ id: c.id, kind: c.kind, text: c.text, sortOrder: c.sort_order });
const mapProjectClause = (c) => ({
  id: c.id, projectId: c.project_id, kind: c.kind, text: c.text,
  sortOrder: c.sort_order, sourceClauseId: c.source_clause_id,
});

export const useBoqClauses = (boqProjectId) => {
  const [library, setLibrary] = useState([]);
  const [clauses, setClauses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [lib, proj] = await Promise.all([
        supabase.schema('boq').from('clause_library')
          .select('id, kind, text, sort_order').eq('is_active', true)
          .order('kind').order('sort_order'),
        boqProjectId
          ? supabase.schema('boq').from('project_clauses')
              .select('id, project_id, kind, text, sort_order, source_clause_id')
              .eq('project_id', boqProjectId).order('kind').order('sort_order')
          : Promise.resolve({ data: [], error: null }),
      ]);
      if (lib.error) throw lib.error;
      if (proj.error) throw proj.error;
      setLibrary((lib.data || []).map(mapLibrary));
      setClauses((proj.data || []).map(mapProjectClause));
    } catch (err) {
      console.error('[useBoqClauses] fetch failed', err);
      setError(formatSupabaseError(err, { action: 'fetch', entity: 'clauses' }));
    } finally {
      setLoading(false);
    }
  }, [boqProjectId]);

  const addClause = useCallback(async ({ kind, text, sourceClauseId = null }) => {
    if (!boqProjectId) return { success: false, error: 'No BOQ project context.' };
    try {
      const nextOrder = clauses.filter((c) => c.kind === kind).length + 1;
      const { data, error: insErr } = await supabase.schema('boq').from('project_clauses')
        .insert({ project_id: boqProjectId, kind, text: text.trim(), sort_order: nextOrder, source_clause_id: sourceClauseId })
        .select('id, project_id, kind, text, sort_order, source_clause_id').single();
      if (insErr) throw insErr;
      const mapped = mapProjectClause(data);
      setClauses((prev) => [...prev, mapped]);
      return { success: true, clause: mapped };
    } catch (err) {
      console.error('[useBoqClauses] add failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'add', entity: 'clause' }) };
    }
  }, [boqProjectId, clauses]);

  const addManyFromLibrary = useCallback(async (libraryIds) => {
    const picks = library.filter((l) => libraryIds.includes(l.id));
    for (const l of picks) {
      // eslint-disable-next-line no-await-in-loop
      await addClause({ kind: l.kind, text: l.text, sourceClauseId: l.id });
    }
    return { success: true };
  }, [library, addClause]);

  const updateClause = useCallback(async (id, text) => {
    try {
      const { data, error: updErr } = await supabase.schema('boq').from('project_clauses')
        .update({ text: text.trim() }).eq('id', id)
        .select('id, project_id, kind, text, sort_order, source_clause_id').single();
      if (updErr) throw updErr;
      const mapped = mapProjectClause(data);
      setClauses((prev) => prev.map((c) => (c.id === id ? mapped : c)));
      return { success: true };
    } catch (err) {
      console.error('[useBoqClauses] update failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'clause' }) };
    }
  }, []);

  const deleteClause = useCallback(async (id) => {
    try {
      const { error: delErr } = await supabase.schema('boq').from('project_clauses').delete().eq('id', id);
      if (delErr) throw delErr;
      setClauses((prev) => prev.filter((c) => c.id !== id));
      return { success: true };
    } catch (err) {
      console.error('[useBoqClauses] delete failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'clause' }) };
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return { library, clauses, loading, error, refresh: fetchAll, addClause, addManyFromLibrary, updateClause, deleteClause };
};
